/**
 * Encodes/decodes chat identifiers for use in URLs.
 * Uses an XOR cipher with a key so neither the phone number nor the optional
 * business id can be read by simply running atob() on the URL parameter — the
 * URL stays an opaque `?chat=...` blob.
 */

const CHAT_URL_PARAM = 'chat';
const CIPHER_KEY = 'h3Lt@r-Ch@t-s3cR3t';
// Separates an optional business id from the number inside the encrypted blob.
const BUSINESS_SEPARATOR = ':';

export interface DecodedChatParam {
  clientWaNumber: string;
  businessId?: number;
}

function xorWithKey(text: string, key: string): string {
  let result = '';
  for (let i = 0; i < text.length; i++) {
    result += String.fromCharCode(
      text.charCodeAt(i) ^ key.charCodeAt(i % key.length),
    );
  }
  return result;
}

export function encodeChatParam(
  clientWaNumber: string,
  businessId?: number,
): string {
  const payload =
    businessId != null
      ? `${businessId}${BUSINESS_SEPARATOR}${clientWaNumber}`
      : clientWaNumber;
  const encrypted = xorWithKey(payload, CIPHER_KEY);
  return btoa(encrypted)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function decodeChatParam(encoded: string): DecodedChatParam {
  const base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = base64.length % 4;
  const paddedBase64 =
    paddingLength === 0 ? base64 : base64 + '='.repeat(4 - paddingLength);
  const payload = xorWithKey(atob(paddedBase64), CIPHER_KEY);

  const sepIndex = payload.indexOf(BUSINESS_SEPARATOR);
  if (sepIndex > 0) {
    const left = payload.slice(0, sepIndex);
    const right = payload.slice(sepIndex + 1);
    if (/^\d+$/.test(left) && right) {
      return { businessId: Number(left), clientWaNumber: right };
    }
  }
  return { clientWaNumber: payload };
}

export { CHAT_URL_PARAM };
